/**
 * Turns the free-text "details for parents" on a consent form into readable
 * blocks.
 *
 * Staff paste these notices from Word or type them in one run, so numbered
 * points arrive inline — "…after 07/09/2026. 2. Students are requested to…" —
 * and sometimes with the marker doubled ("4. 4. Kindly ensure…"). Rendering
 * that raw gives one dense paragraph, which is what parents skim past.
 *
 * Display-only: the stored text is never rewritten, so the admin's own wording
 * survives an edit untouched.
 *
 * Mirrors parseConsentDetails in the API's src/helpers/consentDetailsHelper.ts;
 * the mobile copy is in lib/dashboard/shared/consent_details.dart.
 */

/** "4. 4. Kindly…" → "4. Kindly…" */
const collapseRepeatedMarkers = (input) =>
  input.replace(/(\d+)\.\s*\1\.(?=\s)/g, "$1.");

/**
 * Breaks a run-on notice into lines, starting a new one at each numbered
 * marker. Whitespace is required on both sides, so dates ("07/09/2026") and
 * amounts ("Rs.450/-") are left alone.
 */
const splitOnMarkers = (input) => input.replace(/\s+(\d+\.)\s+/g, "\n$1 ");

export const parseConsentDetails = (details) => {
  if (!details || !details.trim()) return [];

  return splitOnMarkers(collapseRepeatedMarkers(details))
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const match = line.match(/^(\d+)\.\s+(.*)$/);
      return match
        ? { type: "item", marker: `${match[1]}.`, text: match[2] }
        : { type: "text", text: line };
    });
};

/** Renders parsed detail blocks: paragraphs plus a hanging-indent point list. */
export const ConsentDetails = ({ details, className = "" }) => {
  const blocks = parseConsentDetails(details);
  if (!blocks.length) return null;

  return (
    <div className={`grid gap-1 ${className}`}>
      {blocks.map((block, i) =>
        block.type === "item" ? (
          <div key={i} className="flex gap-2">
            <span className="text-gray-500 font-medium shrink-0">
              {block.marker}
            </span>
            <span>{block.text}</span>
          </div>
        ) : (
          <p key={i} className="mb-1">
            {block.text}
          </p>
        )
      )}
    </div>
  );
};
